/**
 * Formik-bound QueryBuilder wrapper for delegated-authority override rules.
 * Sibling of `CommunicationConditionsQueryBuilderFormik` and
 * `RuleConditionsQueryBuilderFormik` (assignments) — same skeleton (read
 * `values[fieldId]`, gate on `submitCount`, call a registry hook, render the
 * pure QB). The LOB and sub-org scope the author picked is forwarded to the
 * catalog, but there is no prerequisite gate: delegated-authority override
 * rules dispatch on no event, and the dialog seeds both pickers to "All", so
 * the catalog fetch fires unconditionally.
 *
 * The composed pure `RuleConditionsQueryBuilder` writes its tree into Formik
 * under `fieldId` (default `'conditions_json'`).
 */

import React, { useEffect } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { getIn, useFormikContext } from 'formik';

import RuleConditionsQueryBuilder from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/RuleConditionsQueryBuilder';
import type { FieldDefinitionDto } from '~/components/SystemConfiguration/AutomaticRulesConfiguration/Conditions/QueryBuilder/types';

import { useDelegatedAuthorityConditionFieldRegistry } from './useDelegatedAuthorityConditionFieldRegistry';

interface DelegatedAuthorityConditionsQueryBuilderFormikProps {
  organizationId: number;
  /** Every LOB the rule is scoped to. Forwarded whole: the catalog is the
   *  cross-LOB intersection, and a rule offered one LOB's fields can be saved
   *  with conditions the other LOBs' claims never carry. Empty when `isAllLobs`. */
  lobs: string[];
  /** True when the rule is scoped to every LOB, present and future. Forwarded as
   *  `is_all_lobs` so the BE expands it the same way the save path does. */
  isAllLobs: boolean;
  isAllSubOrgs: boolean;
  subOrgIds: number[];
  /** Formik path holding the `conditions_json` tree. Default: `'conditions_json'`. */
  fieldId?: string;
  /** Lets the parent form mirror the registry into its own state for use in
   *  Formik's form-level `validate` prop. */
  onFieldsLoaded?: (fields: FieldDefinitionDto[]) => void;
  /** Surfaces the catalog-fetch loading state so the parent dialog can disable
   *  Save while fields are still in flight — mirrors the legacy `Conditions`
   *  control's `setIsLoading` prop. */
  onLoadingChange?: (isLoading: boolean) => void;
}

const DelegatedAuthorityConditionsQueryBuilderFormik: React.FC<DelegatedAuthorityConditionsQueryBuilderFormikProps> = ({
  organizationId,
  lobs,
  isAllLobs,
  isAllSubOrgs,
  subOrgIds,
  fieldId = 'conditions_json',
  onFieldsLoaded,
  onLoadingChange,
}) => {
  const { values, setFieldValue, submitCount } = useFormikContext();
  const value = (getIn(values, fieldId) as RuleGroupType | null | undefined) ?? null;

  const { fields, isLoading } = useDelegatedAuthorityConditionFieldRegistry({
    organizationId,
    lobs,
    isAllLobs,
    isAllSubOrgs,
    subOrgIds,
  });

  // Intentionally only depends on `fields` — `onFieldsLoaded` identity is
  // irrelevant. Including it would silently infinite-loop if a caller passes
  // a non-memoized inline arrow.
  useEffect(() => {
    onFieldsLoaded?.(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <RuleConditionsQueryBuilder
      value={value}
      onChange={(q) => setFieldValue(fieldId, q)}
      shouldShowErrors={submitCount > 0}
      fields={fields}
      isLoading={isLoading}
      prerequisiteMessage={null}
      emptyMessage="No condition fields available."
    />
  );
};

export default DelegatedAuthorityConditionsQueryBuilderFormik;
